import React, { useState, useEffect, useCallback } from 'react';
import { useOpenInCanvas } from '../contexts/GlobalCanvasContext';
import { DashboardEmail, DashboardClientDeal } from '../types/api';
import LexAIService from '../services/api';

interface PersonDetailsProps {
  person: any;
}

// Mercury deal status colors
const DEAL_STATUS_COLORS: Record<string, string> = {
  'Information Gathering': '#3b82f6',
  'Broker Solution Pending': '#f59e0b',
  'File Preparation': '#ea580c',
  'Broker Solution': '#8b5cf6',
  'To be Lodged': '#6366f1',
  'Lodged': '#06b6d4',
  'Conditional - Pre Approval': '#14b8a6',
  'Unconditional': '#10b981',
  'Pending Settlement': '#059669',
};

const PersonDetails: React.FC<PersonDetailsProps> = ({ person }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'deals' | 'emails' | 'activity'>('overview');
  const { openOpportunity, openEmail } = useOpenInCanvas();

  // Load deals from person data (parsed from client.md)
  const clientDeals: DashboardClientDeal[] = person?.deals || [];

  // Email state
  const [clientEmails, setClientEmails] = useState<DashboardEmail[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsLoaded, setEmailsLoaded] = useState(false);

  const loadEmails = useCallback(async () => {
    const email = person?.email;
    if (!email || emailsLoaded) return;

    setEmailsLoading(true);
    try {
      const emails = await LexAIService.searchEmailsByContact(email);
      setClientEmails(emails);
      setEmailsLoaded(true);
    } catch (err) {
      console.error('Failed to load client emails:', err);
    } finally {
      setEmailsLoading(false);
    }
  }, [person?.email, emailsLoaded]);

  // Fetch emails when the emails tab is selected
  useEffect(() => {
    if (activeTab === 'emails' && !emailsLoaded) {
      loadEmails();
    }
  }, [activeTab, emailsLoaded, loadEmails]);

  // Reset emails when person changes
  useEffect(() => {
    setClientEmails([]);
    setEmailsLoaded(false);
  }, [person?.id, person?.email]);

  if (!person) {
    return (
      <div className="empty-canvas">
        <span className="material-symbols-outlined">person_off</span>
        <h3>Person Not Found</h3>
        <p>No person data available</p>
      </div>
    );
  }

  // Generate initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Status color mapping
  const getStatusColor = (status: string) => {
    return DEAL_STATUS_COLORS[status] || '#6b7280';
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Compute financial summary from deals
  const totalDealValue = clientDeals.reduce((sum, deal) => sum + (deal.amount || 0), 0);
  const totalIncome = (person.employment || []).reduce((sum: number, emp: any) => sum + (emp.income || 0), 0);
  const totalExpenses = (person.expenses || []).reduce((sum: number, exp: any) => sum + (exp.amount || 0), 0);

  // Aggregate assets and liabilities across all deals, deduplicating by name+type
  // (same assets/liabilities carry through across deals in Mercury)
  const allAssets = (() => {
    const seen = new Map<string, any>();
    clientDeals.flatMap(d => d.assets || []).forEach(a => {
      const key = `${(a.name || '').toLowerCase().trim()}|${(a.type || '').toLowerCase().trim()}`;
      seen.set(key, a); // later deal overwrites earlier (keeps most recent value)
    });
    return Array.from(seen.values());
  })();
  const allLiabilities = (() => {
    const seen = new Map<string, any>();
    clientDeals.flatMap(d => d.liabilities || []).forEach(l => {
      const key = `${(l.name || '').toLowerCase().trim()}|${(l.type || '').toLowerCase().trim()}`;
      seen.set(key, l);
    });
    return Array.from(seen.values());
  })();
  const totalAssetValue = allAssets.reduce((sum, a) => sum + (a.value || 0), 0);
  const totalLiabilityValue = allLiabilities.reduce((sum, l) => sum + (l.value || 0), 0);

  // Display name with title
  const displayName = person.title
    ? `${person.title} ${person.name || person.displayName}`
    : (person.name || person.displayName);

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: 'var(--obsidian-bg)'
    }}>
      {/* Header Section */}
      <div style={{
        background: 'var(--obsidian-sidebar-bg)',
        borderBottom: '1px solid var(--obsidian-border)',
        padding: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem' }}>
          {/* Avatar */}
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
            fontWeight: '600',
            color: 'white',
            flexShrink: 0
          }}>
            {getInitials(person.name || person.displayName || 'U')}
          </div>

          {/* Header Info */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
                {displayName}
              </h2>
              {person.status && (
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  background: person.status === 'Active' ? '#10b98120' : '#6b728020',
                  color: person.status === 'Active' ? '#10b981' : '#6b7280',
                  border: `1px solid ${person.status === 'Active' ? '#10b98140' : '#6b728040'}`
                }}>
                  {person.status}
                </span>
              )}
            </div>

            {person.middleName && (
              <div style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)', marginBottom: '0.5rem' }}>
                Middle name: {person.middleName}
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginTop: '0.75rem'
            }}>
              {person.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', color: 'var(--obsidian-text-secondary)' }}>mail</span>
                  <span style={{ fontSize: '14px' }}>{person.email}</span>
                </div>
              )}
              {person.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', color: 'var(--obsidian-text-secondary)' }}>phone</span>
                  <span style={{ fontSize: '14px' }}>{person.phone}</span>
                </div>
              )}
              {person.address && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', color: 'var(--obsidian-text-secondary)' }}>location_on</span>
                  <span style={{ fontSize: '14px' }}>{person.address}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                background: 'var(--obsidian-accent)',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>mail</span>
                Send Email
              </button>
              <button style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid var(--obsidian-border)',
                background: 'transparent',
                color: 'var(--obsidian-text-primary)',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>call</span>
                Call
              </button>
              <button style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid var(--obsidian-border)',
                background: 'transparent',
                color: 'var(--obsidian-text-primary)',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>event</span>
                Schedule Meeting
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        padding: '1rem 2rem 0',
        borderBottom: '1px solid var(--obsidian-border)',
        background: 'var(--obsidian-bg)'
      }}>
        {[
          { id: 'overview', label: 'Overview', icon: 'dashboard' },
          { id: 'deals', label: `Deals (${clientDeals.length})`, icon: 'monetization_on' },
          { id: 'emails', label: `Emails${emailsLoaded ? ` (${clientEmails.length})` : ''}`, icon: 'mail' },
          { id: 'activity', label: 'Activity', icon: 'history' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: '0.75rem 1rem',
              border: 'none',
              background: activeTab === tab.id ? 'var(--obsidian-sidebar-bg)' : 'transparent',
              color: activeTab === tab.id ? 'var(--obsidian-text-primary)' : 'var(--obsidian-text-secondary)',
              borderBottom: activeTab === tab.id ? '2px solid var(--obsidian-accent)' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 150ms'
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '2rem'
      }}>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Top row: Contact Details + Financial Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
              {/* Contact Details Card */}
              <div style={{
                background: 'var(--obsidian-sidebar-bg)',
                border: '1px solid var(--obsidian-border)',
                borderRadius: '8px',
                padding: '1.5rem'
              }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined">person</span>
                  Contact Details
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {person.email && (
                    <InfoRow icon="mail" label="Email" value={person.email} />
                  )}
                  {person.phone && (
                    <InfoRow icon="phone" label="Phone" value={person.phone} />
                  )}
                  {person.address && (
                    <InfoRow icon="location_on" label="Address" value={person.address} />
                  )}
                  {person.dateOfBirth && (
                    <InfoRow icon="cake" label="Date of Birth" value={formatDate(person.dateOfBirth)} />
                  )}
                  {person.preferredName && (
                    <InfoRow icon="badge" label="Preferred Name" value={person.preferredName} />
                  )}
                  {person.clientFolder && (
                    <InfoRow icon="folder" label="Client Folder" value={person.clientFolder} />
                  )}
                  {person.lastSync && (
                    <InfoRow icon="sync" label="Last Synced" value={formatDate(person.lastSync)} />
                  )}
                </div>
              </div>

              {/* Financial Summary Card */}
              <div style={{
                background: 'var(--obsidian-sidebar-bg)',
                border: '1px solid var(--obsidian-border)',
                borderRadius: '8px',
                padding: '1.5rem'
              }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined">account_balance</span>
                  Financial Summary
                </h3>
                {(totalDealValue > 0 || totalIncome > 0 || totalExpenses > 0 || totalAssetValue > 0 || totalLiabilityValue > 0) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {totalDealValue > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--obsidian-border)' }}>
                        <span style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)' }}>Total Deal Value</span>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#10b981' }}>{formatCurrency(totalDealValue)}</span>
                      </div>
                    )}
                    {totalIncome > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--obsidian-border)' }}>
                        <span style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)' }}>Total Income (p.a.)</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#10b981' }}>{formatCurrency(totalIncome)}</span>
                      </div>
                    )}
                    {totalExpenses > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--obsidian-border)' }}>
                        <span style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)' }}>Total Expenses</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#ef4444' }}>{formatCurrency(totalExpenses)}</span>
                      </div>
                    )}
                    {totalAssetValue > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--obsidian-border)' }}>
                        <span style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)' }}>Total Assets</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#10b981' }}>{formatCurrency(totalAssetValue)}</span>
                      </div>
                    )}
                    {totalLiabilityValue > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--obsidian-border)' }}>
                        <span style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)' }}>Total Liabilities</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#ef4444' }}>{formatCurrency(totalLiabilityValue)}</span>
                      </div>
                    )}
                    {(totalAssetValue > 0 || totalLiabilityValue > 0) && (
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', padding: '0.75rem',
                        background: (totalAssetValue - totalLiabilityValue) >= 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                        borderRadius: '6px', marginTop: '0.25rem'
                      }}>
                        <span style={{ fontSize: '13px', fontWeight: '600' }}>Net Position</span>
                        <span style={{
                          fontSize: '16px', fontWeight: '700',
                          color: (totalAssetValue - totalLiabilityValue) >= 0 ? '#10b981' : '#ef4444'
                        }}>
                          {formatCurrency(totalAssetValue - totalLiabilityValue)}
                        </span>
                      </div>
                    )}
                    <div style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)', marginTop: '0.25rem' }}>
                      {clientDeals.length} deal{clientDeals.length !== 1 ? 's' : ''}
                      {allAssets.length > 0 && ` | ${allAssets.length} asset${allAssets.length !== 1 ? 's' : ''}`}
                      {allLiabilities.length > 0 && ` | ${allLiabilities.length} liabilit${allLiabilities.length !== 1 ? 'ies' : 'y'}`}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)', textAlign: 'center', padding: '1rem 0' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '24px', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }}>sync</span>
                    No financial data yet. Sync with Mercury to populate income, expenses, assets and liabilities.
                  </div>
                )}
              </div>
            </div>

            {/* Employment Card */}
            {person.employment?.length > 0 && (
              <div style={{
                background: 'var(--obsidian-sidebar-bg)',
                border: '1px solid var(--obsidian-border)',
                borderRadius: '8px',
                padding: '1.5rem'
              }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined">work</span>
                  Employment
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {person.employment.map((emp: any, i: number) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
                      {emp.employer && (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', marginBottom: '2px' }}>Employer</div>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>{emp.employer}</div>
                        </div>
                      )}
                      {emp.position && (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', marginBottom: '2px' }}>Position</div>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>{emp.position}</div>
                        </div>
                      )}
                      {emp.income > 0 && (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', marginBottom: '2px' }}>Income</div>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>{formatCurrency(emp.income)}/yr</div>
                        </div>
                      )}
                      {emp.type && (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', marginBottom: '2px' }}>Type</div>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>{emp.type}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Expenses Card */}
            {person.expenses?.length > 0 && (
              <div style={{
                background: 'var(--obsidian-sidebar-bg)',
                border: '1px solid var(--obsidian-border)',
                borderRadius: '8px',
                padding: '1.5rem'
              }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined">receipt_long</span>
                  Expenses
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--obsidian-border)' }}>
                      <th style={{ textAlign: 'left', padding: '0.5rem 0', fontSize: '12px', color: 'var(--obsidian-text-secondary)', fontWeight: '500' }}>Category</th>
                      <th style={{ textAlign: 'right', padding: '0.5rem 0', fontSize: '12px', color: 'var(--obsidian-text-secondary)', fontWeight: '500' }}>Amount</th>
                      <th style={{ textAlign: 'right', padding: '0.5rem 0', fontSize: '12px', color: 'var(--obsidian-text-secondary)', fontWeight: '500' }}>Frequency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {person.expenses.map((exp: any, i: number) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--obsidian-border)' }}>
                        <td style={{ padding: '0.5rem 0', fontSize: '14px' }}>{exp.category || 'Other'}</td>
                        <td style={{ padding: '0.5rem 0', fontSize: '14px', textAlign: 'right', fontWeight: '500' }}>
                          {exp.amount ? formatCurrency(exp.amount) : '-'}
                        </td>
                        <td style={{ padding: '0.5rem 0', fontSize: '13px', textAlign: 'right', color: 'var(--obsidian-text-secondary)' }}>
                          {exp.frequency || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mercury Notes */}
            <div style={{
              background: 'var(--obsidian-sidebar-bg)',
              border: '1px solid var(--obsidian-border)',
              borderRadius: '8px',
              padding: '1.5rem'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="material-symbols-outlined">note</span>
                Mercury CRM Notes
              </h3>
              {person.mercuryNotes && person.mercuryNotes.replace(/<[^>]*>/g, '').trim() ? (
                <pre style={{
                  margin: 0,
                  padding: '1rem',
                  background: 'var(--obsidian-bg)',
                  borderRadius: '6px',
                  border: '1px solid var(--obsidian-border)',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'inherit',
                  color: 'var(--obsidian-text-primary)',
                  maxHeight: '300px',
                  overflow: 'auto'
                }}>
                  {person.mercuryNotes}
                </pre>
              ) : (
                <div style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)', textAlign: 'center', padding: '1rem 0' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '24px', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }}>sync</span>
                  No Mercury notes yet. Sync with Mercury to pull contact notes.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Deals Tab */}
        {activeTab === 'deals' && (
          <div>
            {clientDeals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--obsidian-text-secondary)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.3 }}>monetization_on</span>
                <p>No deals found for this client</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {clientDeals.map((deal, index) => {
                  const statusColor = getStatusColor(deal.status || '');
                  const dealAssets = deal.assets || [];
                  const dealLiabilities = deal.liabilities || [];
                  const dealApplicants = deal.applicants || [];

                  return (
                    <div
                      key={deal.folder || `deal-${index}`}
                      style={{
                        background: 'var(--obsidian-sidebar-bg)',
                        border: '1px solid var(--obsidian-border)',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        transition: 'all 150ms',
                        cursor: 'pointer'
                      }}
                      onClick={() => openOpportunity(deal)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--obsidian-accent)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--obsidian-border)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      {/* Deal Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                        <div>
                          <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '16px', fontWeight: '600' }}>{deal.name}</h4>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            {deal.transactionType && (
                              <span style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)' }}>{deal.transactionType}</span>
                            )}
                            {deal.transactionSubType && (
                              <span style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', opacity: 0.8 }}>
                                ({deal.transactionSubType})
                              </span>
                            )}
                          </div>
                        </div>
                        {deal.status && (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: `${statusColor}20`,
                            color: statusColor,
                            border: `1px solid ${statusColor}40`,
                            whiteSpace: 'nowrap'
                          }}>
                            {deal.status}
                          </span>
                        )}
                      </div>

                      {/* Deal Info Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
                        {deal.amount != null && deal.amount > 0 && (
                          <div>
                            <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)' }}>Amount</div>
                            <div style={{ fontSize: '18px', fontWeight: '600', color: '#10b981' }}>{formatCurrency(deal.amount)}</div>
                          </div>
                        )}
                        {dealApplicants.length > 0 && (
                          <div>
                            <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)' }}>Applicants</div>
                            <div style={{ fontSize: '13px', fontWeight: '500' }}>
                              {dealApplicants.map(a => a.name).join(', ')}
                            </div>
                          </div>
                        )}
                        {(dealAssets.length > 0 || dealLiabilities.length > 0) && (
                          <div>
                            <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)' }}>Financials</div>
                            <div style={{ fontSize: '13px' }}>
                              {dealAssets.length > 0 && <span style={{ color: '#10b981' }}>{dealAssets.length} asset{dealAssets.length !== 1 ? 's' : ''}</span>}
                              {dealAssets.length > 0 && dealLiabilities.length > 0 && ' | '}
                              {dealLiabilities.length > 0 && <span style={{ color: '#ef4444' }}>{dealLiabilities.length} liabilit{dealLiabilities.length !== 1 ? 'ies' : 'y'}</span>}
                            </div>
                          </div>
                        )}
                        {deal.folder && (
                          <div>
                            <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)' }}>Folder</div>
                            <div style={{ fontSize: '13px', fontWeight: '500' }}>{deal.folder}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Emails Tab */}
        {activeTab === 'emails' && (
          <div>
            {!person?.email ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--obsidian-text-secondary)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.3 }}>mail</span>
                <p>No email address on file for this client</p>
              </div>
            ) : emailsLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--obsidian-text-secondary)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.5, animation: 'spin 1s linear infinite' }}>hourglass_empty</span>
                <p>Searching emails for {person.email}...</p>
              </div>
            ) : clientEmails.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--obsidian-text-secondary)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.3 }}>mail</span>
                <p>No emails found for {person.email}</p>
                <button
                  onClick={() => { setEmailsLoaded(false); loadEmails(); }}
                  style={{
                    marginTop: '0.5rem',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid var(--obsidian-border)',
                    background: 'transparent',
                    color: 'var(--obsidian-text-secondary)',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Retry Search
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)' }}>
                    {clientEmails.length} email{clientEmails.length !== 1 ? 's' : ''} with {person.email}
                  </span>
                  <button
                    onClick={() => { setEmailsLoaded(false); loadEmails(); }}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: '1px solid var(--obsidian-border)',
                      background: 'transparent',
                      color: 'var(--obsidian-text-secondary)',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>refresh</span>
                    Refresh
                  </button>
                </div>
                {clientEmails.map((email, index) => {
                  const emailDate = new Date(email.date);
                  const isFromClient = email.senderEmail?.toLowerCase() === person.email?.toLowerCase();
                  return (
                    <div
                      key={email.id || `email-${index}`}
                      onClick={() => openEmail?.({
                        id: email.id,
                        title: email.subject,
                        subtitle: email.sender,
                        lastModified: emailDate,
                        type: 'email'
                      })}
                      style={{
                        background: 'var(--obsidian-sidebar-bg)',
                        border: '1px solid var(--obsidian-border)',
                        borderRadius: '8px',
                        padding: '1rem 1.25rem',
                        cursor: 'pointer',
                        transition: 'all 150ms',
                        borderLeft: email.isRead ? '3px solid transparent' : '3px solid var(--obsidian-accent)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--obsidian-accent)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--obsidian-border)';
                        e.currentTarget.style.borderLeftColor = email.isRead ? 'transparent' : 'var(--obsidian-accent)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '4px' }}>
                            <span className="material-symbols-outlined" style={{
                              fontSize: '16px',
                              color: isFromClient ? '#3b82f6' : '#10b981'
                            }}>
                              {isFromClient ? 'call_received' : 'call_made'}
                            </span>
                            <span style={{
                              fontSize: '14px',
                              fontWeight: email.isRead ? '400' : '600',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {email.subject}
                            </span>
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--obsidian-text-secondary)', marginBottom: '4px' }}>
                            {isFromClient ? `From ${email.sender}` : `To ${person.name || person.email}`}
                          </div>
                          {email.bodyPreview && (
                            <div style={{
                              fontSize: '12px',
                              color: 'var(--obsidian-text-secondary)',
                              opacity: 0.7,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {email.bodyPreview}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                          <span style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', whiteSpace: 'nowrap' }}>
                            {formatDate(email.date)}
                          </span>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {email.hasAttachments && (
                              <span className="material-symbols-outlined" style={{ fontSize: '14px', color: 'var(--obsidian-text-secondary)' }}>
                                attach_file
                              </span>
                            )}
                            {email.importance === 'High' && (
                              <span className="material-symbols-outlined" style={{ fontSize: '14px', color: '#ef4444' }}>
                                priority_high
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--obsidian-text-secondary)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.3 }}>history</span>
              <p>Activity timeline coming soon</p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

// Reusable info row component
const InfoRow: React.FC<{ icon: string; label: string; value: string }> = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
    <span className="material-symbols-outlined" style={{ fontSize: '18px', color: 'var(--obsidian-text-secondary)', width: '20px' }}>{icon}</span>
    <div>
      <div style={{ fontSize: '12px', color: 'var(--obsidian-text-secondary)', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '14px', fontWeight: '500' }}>{value}</div>
    </div>
  </div>
);

export default PersonDetails;
